import { ADMIN_SESSION_CACHE_KEYS } from '@/shared/constants/cache-keys'
import type { AdminIdentity, AdminSessionSnapshot } from '@/shared/types/admin'

export interface SessionStorageAdapter {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

const getBrowserStorage = (): SessionStorageAdapter | undefined => {
  return typeof localStorage === 'undefined' ? undefined : localStorage
}

const parseAdminIdentity = (value: string | null): AdminIdentity | null => {
  if (!value) {
    return null
  }

  try {
    const parsed: unknown = JSON.parse(value)
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? parsed as AdminIdentity
      : null
  } catch {
    return null
  }
}

export const readAdminSession = (
  storage: SessionStorageAdapter | undefined = getBrowserStorage()
): AdminSessionSnapshot => {
  if (!storage) {
    return { token: '', user: null }
  }

  return {
    token: storage.getItem(ADMIN_SESSION_CACHE_KEYS.token) || '',
    user: parseAdminIdentity(storage.getItem(ADMIN_SESSION_CACHE_KEYS.user))
  }
}

export const writeAdminSession = (
  session: AdminSessionSnapshot,
  storage: SessionStorageAdapter | undefined = getBrowserStorage()
): void => {
  if (!storage) {
    return
  }

  if (session.token) {
    storage.setItem(ADMIN_SESSION_CACHE_KEYS.token, session.token)
  } else {
    storage.removeItem(ADMIN_SESSION_CACHE_KEYS.token)
  }

  if (session.user) {
    storage.setItem(ADMIN_SESSION_CACHE_KEYS.user, JSON.stringify(session.user))
  } else {
    storage.removeItem(ADMIN_SESSION_CACHE_KEYS.user)
  }
}

export const clearAdminSession = (
  storage: SessionStorageAdapter | undefined = getBrowserStorage()
): void => {
  storage?.removeItem(ADMIN_SESSION_CACHE_KEYS.token)
  storage?.removeItem(ADMIN_SESSION_CACHE_KEYS.user)
}
